<template>
    <PageHeader :title="$t('setups.title')" @back="goBack" />

    <div class="px-4">
        <TabBar v-model="selectedTab" :tabs="tabs" />
    </div>
    <div v-if="activeTab === 'setups'" class="flex flex-col gap-6 px-4 py-8">
        <h4 class="font-title text-xs font-extrabold uppercase">{{ $t('setups.my_setups') }}</h4>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Panel size="full" v-if="dartSetups.length < 10">
                <button
                    @click="createSetup"
                    data-testid="setup-empty-slot"
                    class="flex w-full items-center justify-between py-4 text-left"
                >
                    <div>
                        <h3 class="font-title mb-2 text-base font-extrabold uppercase">
                            {{ $t('setups.empty_slot') }}
                        </h3>
                        <p class="text-xs">{{ $t('setups.empty_slot_subtitle') }}</p>
                    </div>
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M14.6667 17.3333L14.6667 24L17.3333 24L17.3333 17.3333L24 17.3333L24 14.6667L17.3333 14.6667L17.3333 8L14.6667 8L14.6667 14.6667L8 14.6667L8 17.3333L14.6667 17.3333Z"
                            fill="#FF2C2C"
                        />
                    </svg>
                </button>
            </Panel>

            <SetupCard
                v-for="(setup, index) in dartSetups"
                :key="setup.id"
                :setup="{ ...setup, number: index + 1 }"
                :setup-stats="setupStats"
                @edit="editSetup(setup)"
                @delete="handleDeleteSetup(setup)"
                @selected="selectSetup(setup)"
            />
        </div>
    </div>

    <div v-else class="flex flex-1 flex-col items-center justify-center px-4 py-8">
        <h3 class="font-title mb-2 text-2xl font-extrabold text-red-500 uppercase">
            {{ $t('setups.explore_setups') }}
        </h3>
        <p class="text-lg text-white">{{ $t('setups.coming_soon') }}</p>
    </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import { useDartSetupStore } from '@/stores/dartSetup.js'
import Panel from '@/components/layout/Panel.vue'
import SetupCard from '@/components/ui/SetupCard.vue'
import TabBar from '@/components/ui/TabBar.vue'

export default {
    components: {
        PageHeader,
        Panel,
        SetupCard,
        TabBar,
    },
    data() {
        return {
            selectedTab: 'setups',
        }
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore, useDartSetupStore),

        tabs() {
            return [
                { id: 'setups', label: this.$t('setups.my_setups') },
                { id: 'explore', label: this.$t('setups.explore') },
            ]
        },

        setupStats() {
            return this.authStore.statistics?.setups || {}
        },

        activeTab() {
            return this.selectedTab
        },

        dartSetups() {
            const setups = this.authStore.dart_setups || []
            return [...setups].sort((a, b) => {
                const statsA = this.setupStats[a.id]
                const statsB = this.setupStats[b.id]

                const gamesA = Object.values(statsA || {}).reduce(
                    (sum, stats) => sum + (stats.games_played || 0),
                    0,
                )
                const gamesB = Object.values(statsB || {}).reduce(
                    (sum, stats) => sum + (stats.games_played || 0),
                    0,
                )

                return gamesB - gamesA
            })
        },
    },
    watch: {
        // Keep ?tab= in step with the visible tab: it makes the tab linkable and lets the router's
        // analytics hook report the right screen name without this view knowing any of them.
        activeTab(tab) {
            const query = tab === 'setups' ? {} : { tab }
            if (this.$route.query.tab !== query.tab) {
                this.$router.replace({ query })
            }
        },
    },
    mounted() {
        if (this.$route.query.tab) {
            this.selectedTab = this.$route.query.tab
        }
    },
    methods: {
        goBack() {
            this.$router.replace({ name: 'home' })
        },
        createSetup() {
            this.dartSetupStore.setEntryPoint({ type: 'route', name: 'setups' })
            this.$router.replace({ name: 'setup-create' })
        },
        editSetup(setup) {
            this.dartSetupStore.setEntryPoint({ type: 'route', name: 'setups' })
            this.$router.replace({ name: 'setup-edit', params: { id: setup.id } })
        },
        selectSetup(setup) {
            console.log('Selected setup:', setup)

            this.dartSetupStore.setEntryPoint({ type: 'route', name: 'setups' })
            this.$router.replace({ name: 'setup-detail', params: { id: setup.id } })
        },
        async handleDeleteSetup(setup) {
            if (!confirm(this.$t('setups.confirm_delete', { name: setup.name }))) {
                return
            }
            try {
                await this.authStore.deleteSetup(setup.id)
                this.$toast(this.$t('setups.delete_success'), 3000)
            } catch (error) {
                console.error('Failed to delete setup:', error)
                const errorMessage =
                    error.response?.data?.message || error.message || this.$t('general.error_retry')
                this.$toast(this.$t('general.error_title'), 5000, errorMessage)
            }
        },
    },
}
</script>
